import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Handles the bundle item removal.
   * @param {string} bundle_id - The bundle UID.
   */
  onBundleLineItemRemove(bundle_id) {
    this.updateBundleQuantity({
      bundle_id,
      quantity: 0,
      action: 'clear',
    });

    const bundleItemsToRemove = this.refs.cartItemRows.filter((row) => {
      return row.dataset.bundleUid === bundle_id;
    });

    // removal animation
    bundleItemsToRemove.forEach((row) => {
      if (prefersReducedMotion()) return;

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    // If there are no other items except for the gift note, clear the cart
    
    let orderNoteHandle = window.Wrapped.options?.order?.product?.handle;
    let productNoteHandle = window.Wrapped.options?.product?.product?.handle;

    let clearCart = false;
    const allLineItems = this.querySelectorAll('[ref="cartItemRows[]"]');

    if (quantity == 0 && (orderNoteHandle || productNoteHandle)) {
      const finalLineItemsQty = allLineItems.length - 1;
      let nonGiftLines = finalLineItemsQty;

      for (let i = 1; i <= allLineItems.length; i++) {
        const lineItemEl = allLineItems[i - 1];
        const json = JSON.parse(lineItemEl.querySelector('[data-ga-item-json]')?.textContent ?? '{}');

        const isGiftLine = (orderNoteHandle && json.handle?.includes(orderNoteHandle))
          || (productNoteHandle && json.handle?.includes(productNoteHandle));

        if (isGiftLine && i != line) {
          nonGiftLines--;
        }
      }

      if (nonGiftLines === 0) {
        clearCart = true;  
      }
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    let fetchEndPoint = '';
    let fetchParams = '';

    if (clearCart) {

      const allVariantIds = Array.prototype.map.call(allLineItems, item => item.dataset.key.split(':')[0]);

      const updates = {};
      for (const variantId of allVariantIds) {
        updates[variantId] = 0;
      }

      fetchEndPoint = `/cart/update.js`;
      fetchParams = JSON.stringify({
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
        updates
      });
    } else {
      fetchEndPoint = `${Theme.routes.cart_change_url}`;
      fetchParams = JSON.stringify({
        line: line,
        quantity: quantity,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });
      
    }

    cartTotal?.shimmer();

    fetch(fetchEndPoint, fetchConfig('json', { body: fetchParams }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Include which line/key changed to allow precise analytics
        // Handle case where refs might be undefined due to wrapped-gift-block injection
        let changedRow = undefined;
        
        if (this.refs.cartItemRows && this.refs.cartItemRows.length > 0) {
          changedRow = this.refs.cartItemRows[line - 1];
        }
        
        // Fallback: if refs are undefined, try to find the row by querying the DOM directly
        if (!changedRow) {
          const cartItemRows = this.querySelectorAll('[ref="cartItemRows[]"]');
          changedRow = /** @type {HTMLTableRowElement | undefined} */ (cartItemRows[line - 1]);
        }
        
        const changedKey = changedRow && changedRow.dataset ? changedRow.dataset.key : undefined;
        let changedItemJson = undefined;
        try {
          const jsonEl = changedRow ? changedRow.querySelector('[data-ga-item-json]') : null;
          if (jsonEl && jsonEl.textContent) changedItemJson = jsonEl.textContent;
        } catch {}
        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
            changedLine: line,
            changedKey,
            changedItemJson,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
        window.Wrapped?.buildExtensions();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Updates the bundle quantity (removes all items in a bundle).
   * @param {Object} config - The config.
   * @param {string} config.bundle_id - The bundle UID.
   * @param {number} config.quantity - The quantity (should be 0 for removal).
   * @param {string} config.action - The action.
   */
  updateBundleQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { bundle_id } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    // Get current cart to find bundle items
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        // Create updates object for all items in the bundle
        const updates = cart.items.reduce((acc, item) => {
          if (item.properties?._bundleUid === bundle_id) {
            acc[item.key] = 0;
          }
          return acc;
        }, {});

        const body = JSON.stringify({
          updates: updates,
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        });

        cartTotal?.shimmer();

        return fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body }));
      })
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          console.error('Bundle removal errors:', parsedResponseText.errors);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Dispatch cart update event (same as regular cart removal)
        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
        window.Wrapped?.buildExtensions();
      })
      .catch((error) => {
        console.error('Bundle removal error:', error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
